import React, { useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EpcEstimateWbsTreeSelect } from '@/components/epc/estimation/EpcEstimateWbsTreeSelect';
import { flattenCbsTree } from '@/utils/cbsPathUtils';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';

export interface EpcCompanyCbsDrawerProps {
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

export const EpcCompanyCbsDrawer: React.FC<EpcCompanyCbsDrawerProps> = ({
  open,
  onClose,
  readOnly,
}) => {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const cbsQ = useQuery({
    queryKey: ['epc-cbs-codes'],
    queryFn: () => epcWbsLibraryService.listCbsCodes(),
    enabled: open,
  });

  const cbsRows = Array.isArray(cbsQ.data) ? cbsQ.data : [];
  const flatRows = useMemo(
    () =>
      flattenCbsTree(
        cbsRows.map((c: any) => ({
          id: String(c.id),
          code: String(c.code),
          name: c.name,
          parent_id: c.parent_id,
        }))
      ),
    [cbsRows]
  );

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) => epcWbsLibraryService.createCbsCode(v),
    onSuccess: () => {
      message.success('CBS code created');
      setModalOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-cbs-codes'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchM = useMutation({
    mutationFn: (p: { id: string; name: string; parentId?: string | null }) =>
      epcWbsLibraryService.patchCbsCode(p.id, { name: p.name, parentId: p.parentId }),
    onSuccess: () => {
      message.success('CBS updated');
      setEditing(null);
      setModalOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-cbs-codes'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => epcWbsLibraryService.deleteCbsCode(id),
    onSuccess: () => {
      message.success('CBS archived');
      void qc.invalidateQueries({ queryKey: ['epc-cbs-codes'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditRow = (row: any) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      parentId: row.parent_id || undefined,
    });
    setModalOpen(true);
  };

  const parentOptions = cbsRows.filter((c: any) => !editing || String(c.id) !== String(editing.id));

  return (
    <Drawer
      title="Company cost breakdown structure"
      open={open}
      onClose={onClose}
      width={620}
      extra={
        !readOnly ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add CBS
          </Button>
        ) : null
      }
    >
      <Table
        size="small"
        rowKey="id"
        loading={cbsQ.isLoading}
        dataSource={flatRows}
        pagination={false}
        columns={[
          {
            title: 'Code',
            dataIndex: 'code',
            width: 140,
            render: (code, row) => (
              <span style={{ paddingLeft: row.depth * 16 }}>{code}</span>
            ),
          },
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'System',
            width: 80,
            render: (_, row) => {
              const full = cbsRows.find((c: any) => String(c.id) === String(row.id));
              return full && Number(full.is_system) ? <Tag>System</Tag> : null;
            },
          },
          ...(readOnly
            ? []
            : [
                {
                  title: '',
                  width: 88,
                  render: (_: unknown, row: any) => {
                    const full = cbsRows.find((c: any) => String(c.id) === String(row.id));
                    if (!full || Number(full.is_system)) return null;
                    return (
                      <Space size={0}>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openEditRow(full)}
                        />
                        <Popconfirm
                          title="Archive CBS code?"
                          onConfirm={() => deleteM.mutate(String(row.id))}
                        >
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    );
                  },
                },
              ]),
        ]}
      />

      <Drawer
        title={editing ? `Edit ${editing.code}` : 'Add CBS code'}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        width={400}
        extra={
          <Button
            type="primary"
            loading={createM.isPending || patchM.isPending}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => {
            if (editing) {
              patchM.mutate({
                id: String(editing.id),
                name: v.name,
                parentId: v.parentId ?? null,
              });
            } else {
              createM.mutate({
                code: v.code,
                name: v.name,
                parentId: v.parentId ?? null,
              });
            }
          }}
        >
          {!editing ? (
            <Form.Item name="code" label="Code" rules={[{ required: true }]}>
              <Input placeholder="e.g. MAT-STEEL" />
            </Form.Item>
          ) : null}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label="Parent CBS (optional)">
            <EpcEstimateWbsTreeSelect
              kind="cbs"
              allowClear
              placeholder="Root level if empty"
              nodes={parentOptions.map((c: any) => ({
                id: String(c.id),
                code: String(c.code),
                name: c.name,
                parent_id: c.parent_id,
              }))}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </Drawer>
  );
};
